import { Link, useLocation, useNavigate } from "react-router-dom";
import { House, ShoppingBasket, ShoppingBag, MessageCircleQuestion, CircleUser } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTradeCart } from "../../hooks/useTradeCart";
import { useAuth } from "../../hooks/useAuth";
import { useShopBannerConfig } from "../../hooks/useShopBannerConfig";
import { useUnreadTicketMessages } from "../../hooks/useUnreadTicketMessages";

import { useMemo } from "react";

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { items } = useTradeCart();
  const { isGuest } = useAuth();
  const { gradientFrom, gradientVia, gradientTo } = useShopBannerConfig();
  const { unreadCount: ticketUnreadCount, firstUnreadTicketId } = useUnreadTicketMessages();
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const navItems = useMemo(() => {
    if (isGuest) {
      // Gäster ser Kassa, Butik (center), Support
      return [
        { path: "/cart", label: "Kassa", icon: ShoppingBasket, badge: totalItems > 0 ? totalItems : undefined },
        { path: "/shop", label: "Butik", icon: ShoppingBag, isCenter: true },
        { path: "/contact", label: "Support", icon: MessageCircleQuestion },
      ];
    }

    const supportPath = ticketUnreadCount > 0
      ? (firstUnreadTicketId ? `/my-tickets/${firstUnreadTicketId}` : "/my-tickets")
      : "/contact";

    // Inloggade: Hem, Kassa, Butik (center), Support, Profil
    return [
      { path: "/", label: "Hem", icon: House },
      { path: "/cart", label: "Kassa", icon: ShoppingBasket, badge: totalItems > 0 ? totalItems : undefined },
      { path: "/shop", label: "Butik", icon: ShoppingBag, isCenter: true },
      { path: supportPath, label: "Support", icon: MessageCircleQuestion, badge: ticketUnreadCount > 0 ? ticketUnreadCount : undefined },
      { path: "/account", label: "Profil", icon: CircleUser },
    ];
  }, [isGuest, totalItems, ticketUnreadCount, firstUnreadTicketId]);

  const isActiveCenter = location.pathname === "/shop";

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50">
      {/* Main nav container */}
      <div className="relative">
        {/* SVG notch background - creates the curved cutout effect */}
        <svg
          className="absolute bottom-0 left-0 right-0 h-[80px] w-full pointer-events-none"
          viewBox="0 0 400 80"
          preserveAspectRatio="none"
          fill="none"
        >
          <path
            d="M0 8 
               L148 8 
               Q160 8 166 20
               Q180 48 200 48
               Q220 48 234 20
               Q240 8 252 8
               L400 8 
               L400 80 
               L0 80 
               Z"
            className="fill-card/80"
          />
        </svg>
        
        {/* Backdrop blur overlay matching the SVG shape */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-[80px] backdrop-blur-2xl pointer-events-none"
          style={{
            WebkitMaskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 80' preserveAspectRatio='none'%3E%3Cpath d='M0 8 L148 8 Q160 8 166 20 Q180 48 200 48 Q220 48 234 20 Q240 8 252 8 L400 8 L400 80 L0 80 Z' fill='black'/%3E%3C/svg%3E")`,
            maskImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 80' preserveAspectRatio='none'%3E%3Cpath d='M0 8 L148 8 Q160 8 166 20 Q180 48 200 48 Q220 48 234 20 Q240 8 252 8 L400 8 L400 80 L0 80 Z' fill='black'/%3E%3C/svg%3E")`,
            WebkitMaskSize: '100% 100%',
            maskSize: '100% 100%',
          }}
        />
        
        {/* Border line following the notch curve */}
        <svg
          className="absolute bottom-0 left-0 right-0 h-[80px] w-full pointer-events-none"
          viewBox="0 0 400 80"
          preserveAspectRatio="none"
          fill="none"
        >
          <path
            d="M0 8 
               L148 8 
               Q160 8 166 20
               Q180 48 200 48
               Q220 48 234 20
               Q240 8 252 8
               L400 8"
            className="stroke-border"
            strokeWidth="1"
            fill="none"
          />
        </svg>
        
        {/* Center circle button - sits inside the notch, aligned with other icons */}
        <Link
          to="/shop"
          className="absolute left-1/2 -translate-x-1/2 bottom-[8px] z-10"
        >
          {/* Gradient ring around button */}
          <div
            className="rounded-full p-[2px]"
            style={{
              background: `linear-gradient(135deg, ${gradientFrom}, ${gradientVia}, ${gradientTo})`
            }}
          >
            <div 
              className={cn(
                "rounded-full flex flex-col items-center justify-center gap-1",
                "bg-gradient-to-br from-muted to-card",
                "transition-all duration-300 ease-out",
                isActiveCenter 
                  ? "w-[80px] h-[80px] shadow-2xl shadow-black/40" 
                  : "w-[72px] h-[72px] shadow-xl hover:scale-105"
              )}
            >
              <ShoppingBag 
                className={cn(
                  "transition-all duration-300 stroke-[1.5]",
                  isActiveCenter 
                    ? "h-8 w-8 text-foreground stroke-[2]" 
                    : "h-7 w-7 text-muted-foreground"
                )} 
              />
              <span 
                className={cn(
                  "text-[10px] font-light leading-none",
                  isActiveCenter 
                    ? "text-foreground font-medium" 
                    : "text-muted-foreground"
                )}
              >
                Butik
              </span>
            </div>
          </div>
        </Link>

        {/* Nav items - positioned in the lower solid part */}
        <div 
          className="relative grid items-center h-[80px]"
          style={{ gridTemplateColumns: isGuest ? '1fr 80px 1fr' : '1fr 1fr 80px 1fr 1fr' }}
        >
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            // Center placeholder - empty space for the circle
            if (item.isCenter) {
              return (
                <div key={item.path} className="flex justify-center" />
              );
            }
            
            // Regular nav items
            return (
              <Link
                key={item.path}
                to={item.path}
                data-cart-target={item.path === '/cart' ? 'true' : undefined}
                className="relative flex flex-col items-center justify-center gap-0.5 px-1 transition-all duration-300 hover:scale-105"
              >
                {/* Icon with badge */}
                <div className="relative">
                  <Icon 
                    className={cn(
                      "h-6 w-6 transition-all duration-300 stroke-[1.5]", 
                      isActive 
                        ? "text-foreground scale-110 stroke-[2]" 
                        : "text-muted-foreground"
                    )} 
                  />
                  {item.badge && (
                    <span className="absolute -top-1.5 -right-1.5 bg-primary text-primary-foreground text-[10px] rounded-full h-4 w-4 flex items-center justify-center font-bold shadow-lg">
                      {item.badge}
                    </span>
                  )}
                </div>
                
                {/* Label */}
                <span 
                  className={cn(
                    "text-[10px] font-light transition-all duration-300 text-center",
                    isActive 
                      ? "text-foreground font-medium" 
                      : "text-muted-foreground"
                  )}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
        
        {/* Safe area padding */}
        <div className="bg-card/80 h-[env(safe-area-inset-bottom)]" />
      </div>
    </nav>
  );
}
